"use server";

import { cookies } from "next/headers";
import {
  getAccessibleClientIds,
  getPermissionSubjectUserId,
  getSession,
  isStaffRole,
} from "@/lib/auth/session";
import {
  searchEntitiesForViewer,
  type GlobalSearchResponse,
} from "@/lib/search/entity-search-server";
import type { SearchFilters } from "@/lib/search/types";
import { searchDemoEntities } from "@/lib/search/demo-search";

/**
 * Global search over records, as a server action.
 *
 * Thin on purpose: it resolves who is asking, routes demo mode to deterministic
 * sample records, then hands off. The authorization itself lives in
 * searchEntitiesForViewer so it can be exercised without a browser session.
 */
export async function globalSearchAction(
  rawQuery: string,
  clientId: string | null,
  filters?: SearchFilters,
): Promise<GlobalSearchResponse> {
  const session = await getSession();
  if (!session) return { sections: [] };

  // Demo mode fabricates the whole product; returning real records here would
  // put real client names on screen during exactly the demo or screen share
  // demo mode exists to protect.
  const cookieStore = await cookies();
  if (cookieStore.get("spark-demo")?.value === "1") {
    return searchDemoEntities(rawQuery, clientId, filters);
  }

  const isStaff = isStaffRole(session.profile.role);
  const accessibleClientIds = isStaff ? undefined : await getAccessibleClientIds(session);

  return searchEntitiesForViewer({
    userId: getPermissionSubjectUserId(session),
    isStaff,
    role: session.profile.role,
    assignedClientIds: accessibleClientIds ?? undefined,
    preset: session.clientMode?.kind === "preset" ? session.clientMode.preset : undefined,
    query: rawQuery,
    clientId,
    filters,
  });
}
